import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Table,
  Form,
  InputGroup,
  Spinner,
  Alert,
  Badge,
  Tab,
  Tabs
} from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { specialBookingsAPI } from '../../api/apiClient';
import "../../assets/css/OrderDetails.css";
import Swal from 'sweetalert2';
import { FaArrowLeft, FaCog, FaFacebookMessenger, FaTrashAlt, FaTruck } from "react-icons/fa";

export default function SpecialBookingDetails() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [booking, setBooking] = useState(null);
  const [activeTab, setActiveTab] = useState("details");
  const [discount, setDiscount] = useState(0);
  const [message, setMessage] = useState('');
  const location = useLocation();
  const fromPage = location.state?.fromPage || 1;

  useEffect(() => {
    fetchBookingDetails();
  }, [id]);

  const fetchBookingDetails = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await specialBookingsAPI.show(id);
      
      if (response.success) {
        setBooking(response.data.data || response.data);
      } else {
        setError(response.message || t('failed_to_fetch_booking_details'));
      }
    } catch (err) {
      setError(t('failed_to_fetch_booking_details'));
      console.error('Error fetching booking details:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    try {
      const response = await specialBookingsAPI.sendNote(id, { message });
      
      if (response.success) {
        Swal.fire({
          icon: "success",
          title: t("success"),
          text: t("booking.notes_submitted"),
          timer: 3000,
          showConfirmButton: false,
        });
        setMessage('');
      } else {
        throw new Error(response.message);
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: t("error"),
        text: err.message || t("failed_to_send_note"),
        timer: 3000,
      });
    }
  };

  const handleDeleteBooking = async () => {
    const result = await Swal.fire({
      title: t('are_you_sure'),
      text: t('confirm_delete_booking'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: t('delete'),
      cancelButtonText: t('cancel')
    });

    if (result.isConfirmed) {
      try {
        const response = await specialBookingsAPI.delete(id);
        
        if (response.success) {
          Swal.fire({
            icon: 'success',
            title: t('deleted'),
            text: t('booking_deleted_successfully'),
            timer: 3000,
            showConfirmButton: false
          });
          navigate('/admin/special-bookings');
        } else {
          throw new Error(response.message);
        }
      } catch (err) {
        Swal.fire({
          icon: 'error',
          title: t('error'),
          text: err.message || t('failed_to_delete_booking'),
          timer: 3000,
        });
      }
    }
  };

  const formatDateTime = (dateTimeArray) => {
    if (!dateTimeArray || !dateTimeArray[0]) return '-';
    return dateTimeArray[0];
  };

  const handleBack = () => {
    navigate('/admin/special-bookings', { 
      state: { fromPage: fromPage } 
    });
  };

    const handleViewTrack = (booking_id) => {
    navigate(`/admin/special-bookings/track/${booking_id}`);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      assigned: { variant: 'warning', text: t('assigned') },
      received: { variant: 'info', text: t('received') },
      completed: { variant: 'success', text: t('completed') },
      pending: { variant: 'secondary', text: t('pending') }
    };
    
    const config = statusConfig[status] || { variant: 'light', text: status };
    return <Badge bg={config.variant}>{config.text}</Badge>;
  };

  if (loading) {
    return (
      <Container fluid className="p-1 bg-light min-vh-100 d-flex justify-content-center align-items-center">
        <Spinner animation="border" />
        <span className="ms-2">{t('loading')}</span>
      </Container>
    );
  }

  if (error) {
    return (
      <Container fluid className="p-1 bg-light min-vh-100">
        <Alert variant="danger" className="m-3">
          {error}
          <Button variant="outline-danger" className="ms-3" onClick={handleBack}>
            {t('back_to_list')}
          </Button>
        </Alert>
      </Container>
    );
  }

  if (!booking) {
    return (
      <Container fluid className="p-1 bg-light min-vh-100">
        <Alert variant="warning" className="m-3">
          {t('booking_not_found')}
          <Button variant="outline-warning" className="ms-3" onClick={handleBack}>
            {t('back_to_list')}
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="p-1 bg-light min-vh-100">
      <Card className="mb-4 shadow-sm card-header">
        <Card.Body className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
          <div className="card-title">
            <h2 className="mb-0">
              {t("booking.details")} - <span className="text-primary">{booking.booking_id}</span>
            </h2>
            <small>{t("booking.overview")}</small>
          </div>
          
          <div className="d-flex flex-wrap gap-2">
              {getStatusBadge(booking.status || 'pending')}

              <small className="text-white">
                {t('booking.created_on')}: {new Date(booking.created_at).toLocaleDateString('de-DE')}
              </small>
          </div>
        </Card.Body>
      </Card>

      {/* Actions Section */}
      <Card className="mb-4 shadow-sm">
        <Card.Header className="fw-bold card-title d-flex align-items-center">
          <FaCog className="me-2" />
          {t("booking.actions_section")}
        </Card.Header>
        <Card.Body>
          <Row>
            {/* إدارة الحالة */}
            <Col md={6} className="mb-3">
              <h6 className="fw-bold mb-3 text-primary">
                <FaCog className="me-2" />
                {t("booking.status_management")}
              </h6>
              <div className="d-flex flex-wrap gap-2 mb-3">
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => handleViewTrack(booking.booking_id)}
                  className="mx-1"
                >
                  <FaTruck /> {t("track")}
                </Button>
              </div>
            </Col>
          </Row>

          {/* أزرار التنقل والإجراءات الخطرة */}
          <Row className="mt-3 pt-3 border-top">
            <Col className="d-flex flex-wrap gap-2 justify-content-between">
              <div className="d-flex flex-wrap gap-2">
                <Button variant="outline-secondary" onClick={handleBack} size="sm">
                  <FaArrowLeft className="me-1" />
                  {t("booking.back")}
                </Button>
                
                <Button 
                  variant="outline-danger" 
                  onClick={handleDeleteBooking}
                  size="sm"
                >
                  <FaTrashAlt className="me-1" />
                  {t("Delete")}
                </Button>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-4" fill>
        <Tab eventKey="details" title={t("booking_details")}>
          <Row>
            <Col md={8}>
              <Card className="mb-4 shadow-sm">
                <Card.Header className="fw-bold card-title">
                  {t("booking.loading_info")} ({booking.booking_from?.length || 0} {t('locations')})
                </Card.Header>
                <Card.Body>
                  {booking.booking_from?.map((address, index) => (
                    <div key={index} className="border p-3 mb-3">
                      <h6>{t('loading_location')} {index + 1}:</h6>
                      <p><strong>{t("booking.loading_company")}:</strong> {booking.loading_company?.[index] || '-'}</p>
                      <p><strong>{t("booking.loading_first_name")}:</strong> {booking.loading_first_name?.[index] || '-'}</p>
                      <p><strong>{t("booking.loading_last_name")}:</strong> {booking.loading_last_name?.[index] || '-'}</p>
                      <p><strong>{t("booking.address")}:</strong> {address}</p>
                      <p><strong>{t("booking.loading_streat_no")}:</strong> {booking.loading_streat_no?.[index] || '-'}</p>
                      <p><strong>{t("booking.loading_address_additional")}:</strong> {booking.loading_address_additional?.[index] || '-'}</p>
                      <p><strong>{t("booking.phone")}:</strong> {booking.loading_phone?.[index] || '-'}</p>
                      <p><strong>{t("booking.loading_reffernce")}:</strong> {booking.loading_reffernce?.[index] || '-'}</p>
                      <p><strong>{t("booking.date")}:</strong> {booking.loading_date_time?.[index]}</p>
                      {/* <p><strong>{t("booking.date")}:</strong> {formatDateTime(booking.loading_date_time?.[index])}</p> */}
                    </div>
                  ))}
                </Card.Body>
              </Card>

              <Card className="mb-4 shadow-sm">
                <Card.Header className="fw-bold card-title">
                  {t("booking.unloading_info")} ({booking.booking_to?.length || 0} {t('locations')})
                </Card.Header>
                <Card.Body>
                  {booking.booking_to?.map((address, index) => (
                    <div key={index} className="border p-3 mb-3">
                      <h6>{t('unloading_location')} {index + 1}:</h6>
                      <p><strong>{t("booking.company")}:</strong> {booking.unloading_company?.[index] || '-'}</p>
                      <p><strong>{t("booking.unloading_first_name")}:</strong> {booking.unloading_first_name?.[index] || '-'}</p>
                      <p><strong>{t("booking.unloading_last_name")}:</strong> {booking.unloading_last_name?.[index] || '-'}</p>
                      <p><strong>{t("booking.address")}:</strong> {address}</p>
                      <p><strong>{t("booking.unloading_streat_no")}:</strong> {booking.unloading_streat_no?.[index] || '-'}</p>
                      <p><strong>{t("booking.unloading_address_additional")}:</strong> {booking.unloading_address_additional?.[index] || '-'}</p>
                      <p><strong>{t("booking.phone")}:</strong> {booking.unloading_phone?.[index] || '-'}</p>
                      <p><strong>{t("booking.unloading_reffernce")}:</strong> {booking.unloading_reffernce?.[index] || '-'}</p>
                      <p><strong>{t("booking.date")}:</strong> {booking.unloading_date_time?.[index]}</p>
                    </div>
                  ))}
                </Card.Body>
              </Card>

              <Card className="mb-4 shadow-sm">
                <Card.Header className="fw-bold card-title">{t("booking.shipment_details")}</Card.Header>
                <Card.Body>
                  <Table striped bordered hover>
                    <thead>
                      <tr>
                        {/* <th>#</th> */}
                        <th>{t("booking.item")}</th>
                        <th>{t("booking.quantity")}</th>
                        <th>{t("booking.dimensions")}</th>
                        <th>{t("booking.weight_per_stack")}</th>
                        <th>{t("booking.total_weight")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(booking.shipment_data || []).map((item, idx) => (
                        <tr key={idx}>
                          {/* <td>{idx + 1}</td> */}
                          <td>{item.shipment_data || 'Palette'}</td>
                          <td>{item.quantity || booking.quantity || 1}</td>
                          <td>
                            {item.length || 0} x {item.width || 0} x {item.height || 0} cm
                          </td>
                          <td>{item.weight || 0} kg</td>
                          <td>
                            {((item.quantity || booking.quantity || 1) * (item.weight || 0)).toFixed(2)} kg
                          </td>
                        </tr>
                      ))}
                      {/* Optionally, show grand total weight */}
                      <tr>
                        <td colSpan={1} className="fw-bold">{t("booking.total_weight")}</td>
                        <td colSpan={5} className="fw-bold text-success">
                          {booking.total_weight}
                        </td>
                      </tr>
                      <tr>
                        <td colSpan={1} className="fw-bold">{t("booking.total_distance")}</td>
                        <td colSpan={5} className="fw-bold">
                          {booking.distance}
                        </td>
                      </tr>
                      <tr>
                        <td colSpan={1} className="fw-bold">{t("duration")}</td>
                        <td colSpan={5} className="fw-bold">
                          {booking.total_duration}
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>

            </Col>

            <Col md={4}>
              <Card className="mb-4 shadow-sm">
                <Card.Header className="fw-bold card-title d-flex justify-content-between align-items-center flex-wrap">
                  {t("booking.driver_info")}
                  <button
                    className="btn btn-success"
                    onClick={() => navigate(`/admin/chat-driver-company/${booking.order.order_driver}`)}
                  >
                    <FaFacebookMessenger className="me-1" />
                  </button>
                </Card.Header>
                <Card.Body>
                  {booking.order ? (
                    <>
                      <p><strong>{t("driver_name")}:</strong> {booking.order.driver_name}</p>
                      <p><strong>{t("phone")}:</strong> {booking.order.phone_number}</p>
                      <p><strong>{t("vehicle_type")}:</strong> {booking.order.vehicle_type}</p>
                      <p><strong>{t("vehicle_number")}:</strong> {booking.order.vehicle_number}</p>
                      <p><strong>{t("current_location")}:</strong> {booking.order.current_location}</p>
                      <p><strong>{t("status")}:</strong> {getStatusBadge(booking.order.status)}</p>
                    </>
                  ) : (
                    <p className="text-muted">{t('no_driver_assigned')}</p>
                  )}
                </Card.Body>
              </Card>

              <Card className="mb-4 shadow-sm">
                <Card.Header className="fw-bold card-title">{t("booking.extra_services")}</Card.Header>
                <Card.Body>
                  {booking.extra_service && booking.extra_service.length > 0 ? (
                    <ul className="list-unstyled">
                      {booking.extra_service.map((service, index) => (
                        <li key={index}>• {t(`${service}_special_services`)}</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-muted">{t("booking.no_service_selected")}</p>
                  )}
                </Card.Body>
              </Card>

              <Card className="mb-4 shadow-sm">
                <Card.Header className="fw-bold card-title">{t("booking.additional_details")}</Card.Header>
                <Card.Body>
                  <Form>
                    <Form.Group controlId="additionalDetails">
                      <Form.Label>{t("booking.notes_label")}</Form.Label>
                      <Form.Control 
                        as="textarea" 
                        rows={4} 
                        placeholder={t("booking.notes_placeholder")}
                        value={booking.message}
                        onChange={(e) => setMessage(e.target.value)}
                      />
                    </Form.Group>
                    <div className="mt-3 d-flex justify-content-end">
                      <Button className="update-btn" onClick={handleSendMessage}>
                        {t("booking.submit_notes")}
                      </Button>
                    </div>
                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Tab>

        <Tab eventKey="logs" title={t("admin_logs")}>
          <div className="table-container shadow-sm rounded">
            <div className="table-responsive">
              <Table bordered hover className="custom-table mb-0 text-center">
                <thead className="table-dark">
                  <tr>
                    {/* <th>{t("id")}</th> */}
                    <th>{t("admin_name")}</th>
                    <th>{t("admin_email")}</th>
                    <th>{t("action")}</th>
                    <th>{t("description")}</th>
                    <th>{t("created_at")}</th>
                  </tr>
                </thead>
                <tbody>
                  {booking.admin_logs.map((entry) => (
                    <tr key={entry.id}>
                      {/* <td>{entry.id}</td> */}
                      <td>{entry.admin_name || "N/A"}</td>
                      <td>{entry.admin_email || "N/A"}</td>
                      <td>{entry.action}</td>
                      <td>{entry.description}</td>
                      <td>{new Date(entry.created_at).toLocaleString('de-DE')}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </div>
        </Tab>
      </Tabs>
    </Container>
  );
}